import { capitalize, isArray } from "lodash";
import moment from "moment";
import { FC, useCallback, useState } from "react";
import { FaFileExcel, FaFilePdf } from "react-icons/fa6";
import { PiBuildingOffice, PiCalendar, PiChatCircle, PiTruck, PiUser } from "react-icons/pi";
import { RiWeightLine } from "react-icons/ri";
import { useParams } from "react-router-dom";
import { Button, Tooltip } from "rizzui";
import { toast } from "sonner";
import { usePrivilege } from "../../../../../components/priviledge/PriviledgeProvider";
import { useFiltersPR } from "../../../../../components/providers/FiltersProvider";
import { FilterDrawerView } from "../../../../../components/table/components/TableFilter";
import CustomTable from "../../../../../components/table/CustomTable";
import useFetch from "../../../../../hooks/useFetch";
import Page from "../../../../../layout/Page/Page";
import { PickupService } from "../../../../../services/pickup/pickupService";
import { PickupsApiResponse } from "../../../../../type/entities/pickup-type";
import cn from "../../../../../utils/classNames";
import { pickupStatuses } from "../../../../../utils/SelectorMapping";
import HeadquarterPickupsFilters from "./HeadquarterPickupsFilters";
import StatusDropdown from "../../../../../components/forms/StatusDropdown";
import { formatDistance } from "date-fns";
import { es } from "date-fns/locale";

const columnOrder = [
    'id',
    'user',
    'route',
    'vehicle',
    'pickupStatus',
    'totalProducts',
    'pickedWeightKg',
    'comments',
    'pickupDate',
    'createdAt',
    'updatedAt'
];

const HeadquarterPickups: FC = () => {
    // STATES
    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const service = new PickupService();
    const { id: headquarterId = '' } = useParams<{ id: string }>();

    // HOOKS
    const { userCan } = usePrivilege();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();

    // FETCH DATA

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTIENE EL LISTADO DE RECOGIDAS FILTRADO POR SEDE
     * @EN GETS THE LIST OF PICKUPS FILTERED BY HEADQUARTERS
     */
    // -------------------------------------------------------------------------------------------------------------
    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        if (!headquarterId) return null;

        // Agregar el filtro de headquarters a los filtros existentes
        const filtersWithHeadquarter = {
            ...filters,
            filter_filters: {
                ...filters.filter_filters,
                headquarter: headquarterId
            }
        };

        const response = await service.listPickups(filtersWithHeadquarter);
        return response.getResponseData() as PickupsApiResponse;
    }, [filters, headquarterId]));
    // -------------------------------------------------------------------------------------------------------------

    // FUNCTIONS
    
    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES EXPORTA LOS DATOS A EXCEL
     * @EN EXPORTS DATA TO EXCEL
     */
    // -------------------------------------------------------------------------------------------------------------
    const handleExportExcel = async () => {
        try {
            const filtersWithHeadquarter = {
                ...filters,
                filter_filters: {
                    ...filters.filter_filters,
                    headquarter: headquarterId
                }
            };

            await service.exportToExcel(filtersWithHeadquarter);
            toast.success('Archivo Excel generado correctamente');
        } catch (error) {
            toast.error('Error al generar el archivo Excel');
        }
    };
    // -------------------------------------------------------------------------------------------------------------


    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES MUESTRA LOS DATOS EN EL ORDEN CORRECTO
     * @EN SHOWS THE DATA IN THE CORRECT ORDER
     */
    // -------------------------------------------------------------------------------------------------------------
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });
    // -------------------------------------------------------------------------------------------------------------

    // RENDER
    return (
        <Page container='fluid'>
            {/* Filtros de estados con Buttons */}
            <div className="flex flex-wrap gap-2">
                <div className="flex flex-wrap bg-[#f48b210d] mb-3 p-2 rounded-lg">
                    <Button
                        type="button"
                        onClick={() => updateFilters({ pickup_status: null })}
                        className={cn("bg-transparent border-none text-gray-600", {
                            "bg-white": !filters?.filter_filters?.pickup_status || isArray(filters?.filter_filters?.pickup_status),
                        })}
                    >
                        Todos
                    </Button>
                    {pickupStatuses.map((status) => {
                        const isChecked = filters?.filter_filters?.pickup_status == status.value;
                        return (
                            <Button
                                key={status.value}
                                type="button"
                                onClick={() => updateFilters({ pickup_status: status.value })}
                                className={cn("bg-transparent border-none text-gray-600", {
                                    "bg-white": isChecked,
                                })}
                            >
                                {status.label}
                            </Button>
                        )
                    })}
                </div>
            </div>

            {data !== undefined && (
                <CustomTable
                    id={'headquarters-pickups-table'}
                    columnOrder={columnOrder}
                    data={orderedData()}
                    isLoading={loading}
                    isSortable
                    columnsNoSortables={[]}
                    columnsNotShown={['createdAt']}
                    customRowsBackgroundColors={
                        pickupStatuses.map((status) => ({
                            column: 'pickupStatus',
                            values: [status.value],
                            gradientBg: status.gradientBg,
                            useGradient: true,
                        }))
                    }
                    overrideColumns={[
                        {
                            key: 'user',
                            columnNameKey: 'Usuario Ejecutor',
                            render: (row: any) => {
                                const user = row.row.original?.user;
                                return (
                                    <div className="flex items-center gap-2 text-sm text-gray-900">
                                        <PiUser className="text-gray-400 min-h-[25px] min-w-[25px]" />
                                        <div className="flex flex-col">
                                            <span className="font-medium">
                                                {user ? `${user.name} ${user.lastName}` : 'Sin asignar'}
                                            </span>
                                            {user?.email && (
                                                <span className="text-xs text-gray-500">{user.email}</span>
                                            )}
                                        </div>
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'route',
                            columnNameKey: 'Ruta',
                            render: (row: any) => {
                                const route = row.row.original?.route;
                                return (
                                    <div className="flex items-center gap-2 text-sm text-gray-900">
                                        <PiBuildingOffice className="text-gray-400 min-h-[25px] min-w-[25px]" />
                                        <span className="font-medium">
                                            {route?.name || 'Sin ruta'}
                                        </span>
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'vehicle',
                            columnNameKey: 'Vehículo',
                            render: (row: any) => {
                                const vehicle = row.row.original?.vehicle;
                                const vehicleInfo = vehicle ? `${vehicle.brand} ${vehicle.model}` : null;
                                const fullInfo = vehicle ? `${vehicle.brand} ${vehicle.model} (${vehicle.plateNumber})` : null;
                                return (
                                    <div className="flex items-center gap-2 text-sm text-gray-900">
                                        {fullInfo ? (
                                            <div className="flex flex-col">
                                                <div className="font-bold text-nowrap gap-3 flex flex-row items-center">
                                                    <PiTruck className="text-gray-400 min-h-[25px] min-w-[25px]" />
                                                    {vehicleInfo}
                                                </div>
                                                <div className="text-sm text-gray-500 text-nowrap">
                                                    <span className="font-bold">Matricula: </span>
                                                    {vehicle?.plateNumber ?? 'N/A'}
                                                </div>
                                            </div>
                                        ) : (
                                            <span>Sin asignar</span>
                                        )}
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'pickupStatus',
                            columnNameKey: 'Estado',
                            render: (row: any) => {
                                const status = row.row.original?.pickupStatus;
                                const statusOption = pickupStatuses.find((item) => item.value === status);
                                if (!status) return 'Sin estado';
                                return (
                                    <div className='flex flex-col gap-2 justify-content-center text-center pointer-events-none' key={row.row.original.id + status}>
                                        <StatusDropdown
                                            entityId={row.row.original?.id}
                                            title="¿Estas seguro de cambiar el estado?"
                                            statesOptions={pickupStatuses}
                                            currentState={{
                                                name: statusOption?.label,
                                                id: statusOption?.value,
                                                color: statusOption?.color
                                            }}
                                            handleStateChange={(entityId: string, status: any, comment: string) => console.log(entityId, status, comment)}
                                            disabled={!userCan('edit', 'vehicle') || status === 'sold'}
                                        />
                                    </div>
                                );
                            },
                        },
                        {
                            key: 'totalProducts',
                            columnNameKey: 'Productos Entregados',
                            render: (row: any) => {
                                const totalProducts = row.row.original?.pickupProducts?.length || 0;
                                return (
                                    <div className="flex items-center gap-2 text-sm text-gray-900">
                                        <span className="font-medium">
                                            {totalProducts} producto{totalProducts !== 1 ? 's' : ''}
                                        </span>
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'pickedWeightKg',
                            columnNameKey: 'Peso Recogido',
                            render: (row: any) => {
                                const weight = row.row.original?.pickedWeightKg;
                                return (
                                    <div className="flex items-center gap-2 text-sm text-gray-900">
                                        <RiWeightLine className="text-gray-400 min-h-[25px] min-w-[25px]" />
                                        <span className="font-medium">
                                            {weight !== null && weight !== undefined ? `${weight} kg` : 'N/A'}
                                        </span>
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'comments',
                            columnNameKey: 'Comentarios',
                            render: (row: any) => {
                                const comments = row.row.original?.comments?.trim();
                                return (
                                    <div className="text-sm text-gray-500 max-w-xs">
                                        <div className="flex items-center gap-2">
                                            <PiChatCircle className="text-gray-400 flex-shrink-0" />
                                            <span className="line-clamp-3 max-w-[200px]">{comments || 'Sin comentarios'}</span>
                                        </div>
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'pickupDate',
                            columnNameKey: 'Fecha de Recogida',
                            render: (row: any) => {
                                const status = row.row.original?.pickupStatus;
                                const pickupDate = row.row.original?.pickupDate?.date;
                                const isPlanned = status === 'planned' || status === 'rescheduled';
                                if (isPlanned) {
                                    return (
                                        <div className="flex items-center gap-2 text-sm text-gray-900">
                                            <PiCalendar className="text-gray-400 min-h-[20px] min-w-[20px]" />
                                            <div>
                                                <span className="text-gray-500 block text-xs">Sin visita</span>
                                                <span>{pickupDate ? moment(pickupDate).format('DD/MM/YYYY') : 'N/A'}</span>
                                            </div>
                                        </div>
                                    );
                                }
                                return (
                                    <div className="flex items-center gap-2 text-sm text-gray-900">
                                        <PiCalendar className="text-gray-400 min-h-[25px] min-w-[25px]" />
                                        <div className="flex flex-col">
                                            <span className="font-medium">
                                                {pickupDate ? moment(pickupDate).format('DD/MM/YYYY') : 'N/A'}
                                            </span>
                                        </div>
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'updatedAt',
                            render: (row: any) => {
                                const date = row.row.original?.updatedAt?.date;
                                return date ? (
                                    <div className="text-sm text-gray-500">
                                        <Tooltip content={moment(date).format('DD/MM/YYYY HH:mm:ss')}>
                                            <span>{capitalize(formatDistance(new Date(date), new Date(), { addSuffix: true, locale: es }))}</span>
                                        </Tooltip>
                                    </div>
                                ) : 'Nunca';
                            },
                        },
                    ]}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: (data as PickupsApiResponse) ? data.lastPage : 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                >
                    {userCan('export', 'pickup') && (
                        <Button
                            variant="solid"
                            size="sm"
                            onClick={() => handleExportExcel()}
                            className="p-4 bg-green-600"
                        >
                            <FaFileExcel className="mr-2 min-h-[20px] min-w-[20px]" />
                            Exportar Excel
                        </Button>
                    )}
                </CustomTable>
            )}

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros Recogidas'}>
                <HeadquarterPickupsFilters filters={filters} updateFilters={updateFilters} />
            </FilterDrawerView>
        </Page>
    );
};

export default HeadquarterPickups;